// Smart compact summary of an auto-transaction rule's condition and actions
// for the admin list. We render a short single-line preview (truncated with
// "+N more" past a length budget) and a longer multi-line text for the row's
// tooltip.

import type {
  ConditionalActionMatcherDto,
  ConditionDto,
  EditActionDto,
  FieldName,
  Operator,
} from "../api/types";

export type RuleSummary = {
  /** One-line preview suitable for a row's secondary text. */
  short: string;
  /** Full multi-line text for a hover tooltip. */
  full: string;
};

/// Display-name resolver for the entity-id-valued action variants. Pass one
/// that reads from the registry by id; absent ids fall through to the raw
/// id string, which keeps the summary readable even when the registry is
/// still loading.
export type NameResolvers = {
  source: (id: string) => string | undefined;
  group: (id: string) => string | undefined;
  category: (id: string) => string | undefined;
  expenseType: (id: string) => string | undefined;
  /// Library entries a rule can cite. Optional so callers that only have the
  /// entity registry still type-check; an unresolved set falls back to its id.
  matchSet?: (id: string) => string | undefined;
  actionSet?: (id: string) => string | undefined;
};

const renderEntityValue = (
  resolve: (id: string) => string | undefined,
  id: string,
): string => resolve(id) ?? id;

const truncId = (id: string) => (id.length > 8 ? `${id.slice(0, 8)}…` : id);

const SHORT_LINE_BUDGET = 80;

// ----- conditions ----------------------------------------------------------

const FIELD_LABEL: Record<FieldName, string> = {
  source: "source",
  merchant: "merchant",
  description: "desc",
  group: "group",
  amount: "amount",
  expenseType: "type",
  datePaid: "date",
  kid: "KID",
  payToAccount: "pay-to",
};

const OPERATOR_SYMBOL: Record<Operator, string> = {
  equal: "=",
  contains: "⊃",
  regex: "~",
  is: "=",
  isOneOf: "∈",
  greaterThan: ">",
  lessThan: "<",
  between: "in",
  onDay: "on",
  betweenDays: "in",
  dateBetween: "in",
};

const isNumericField = (f: FieldName) => f === "amount";
const isDateField = (f: FieldName) => f === "datePaid";

/// The entity-valued conditions carry ids, like the entity-valued actions, so
/// they read as names here or fall back to the raw id while the registry loads.
const entityResolver = (
  field: FieldName,
  resolvers: NameResolvers | undefined,
): ((id: string) => string | undefined) | undefined => {
  if (!resolvers) return undefined;
  switch (field) {
    case "source":
      return resolvers.source;
    case "group":
      return resolvers.group;
    case "expenseType":
      return resolvers.expenseType;
    default:
      return undefined;
  }
};

const formatFieldValue = (
  field: FieldName,
  op: Operator,
  value: string,
  resolvers?: NameResolvers,
) => {
  if (op === "between" || op === "betweenDays") {
    const [min = "", max = ""] = value.split(",");
    return `[${min.trim()}, ${max.trim()}]`;
  }
  if (op === "is" || op === "isOneOf") {
    const resolve = entityResolver(field, resolvers);
    const names = value
      .split(",")
      .filter(Boolean)
      .map((id) => `"${resolve ? renderEntityValue(resolve, id) : id}"`);
    if (names.length === 0) return "(none)";
    return op === "isOneOf" ? `{${names.join(", ")}}` : names[0];
  }
  if (isNumericField(field) || isDateField(field)) return value;
  return `"${value}"`;
};

const renderFieldCondition = (
  field: FieldName,
  op: Operator,
  value: string,
  resolvers?: NameResolvers,
): string =>
  `${FIELD_LABEL[field]} ${OPERATOR_SYMBOL[op]} ${formatFieldValue(field, op, value, resolvers)}`;

/// A condition on a field that no longer exists. It never matches and the
/// rule is reported dead, so it reads as what it is rather than as a value.
const renderRetired = (field: string): string => `${field} (retired)`;

const joinerFor = (type: "all" | "any") => (type === "all" ? " AND " : " OR ");

/// A cited library entry reads as its name, not its contents: the point of a
/// set is that the rule defers to it, and inlining the expansion here would
/// hide the very indirection the reader needs to see.
const renderRef = (id: string, resolvers?: NameResolvers): string =>
  `match set "${resolvers?.matchSet?.(id) ?? truncId(id)}"`;

/** Inline rendering — wraps groups in parens so it stays unambiguous. */
const renderConditionInline = (
  c: ConditionDto,
  resolvers?: NameResolvers,
): string => {
  if (c.type === "field")
    return renderFieldCondition(c.field, c.operator, c.value, resolvers);
  if (c.type === "ref") return renderRef(c.set, resolvers);
  if (c.type === "retired") return renderRetired(c.field);
  const parts = c.conditions.map((sub) =>
    sub.type === "all" || sub.type === "any"
      ? `(${renderConditionInline(sub, resolvers)})`
      : renderConditionInline(sub, resolvers),
  );
  return parts.join(joinerFor(c.type));
};

/** Multi-line rendering for tooltips — one condition per line, indented. */
const renderConditionMultiline = (
  c: ConditionDto,
  indent = 0,
  resolvers?: NameResolvers,
): string => {
  const pad = "  ".repeat(indent);
  if (c.type === "field")
    return `${pad}${renderFieldCondition(c.field, c.operator, c.value, resolvers)}`;
  if (c.type === "ref") return `${pad}${renderRef(c.set, resolvers)}`;
  if (c.type === "retired") return `${pad}${renderRetired(c.field)}`;
  const op = c.type === "all" ? "AND" : "OR";
  if (c.conditions.length === 0) return `${pad}(empty ${op})`;
  const body = c.conditions
    .map((sub) => renderConditionMultiline(sub, indent + 1, resolvers))
    .join(`\n${pad}  ${op}\n`);
  return body;
};

/**
 * Greedily concatenates rendered items joined by `sep` until the budget is
 * exceeded, then appends "· +N more". The first item is always kept even when
 * it overruns on its own, so empty output is impossible when items is non-empty.
 */
const truncateJoin = (items: string[], sep: string, budget: number): string => {
  if (items.length === 0) return "";
  const kept: string[] = [items[0]];
  let len = items[0].length;
  for (let i = 1; i < items.length; i += 1) {
    const next = sep + items[i];
    if (len + next.length > budget) break;
    kept.push(items[i]);
    len += next.length;
  }
  const dropped = items.length - kept.length;
  const joined = kept.join(sep);
  return dropped > 0 ? `${joined} · +${dropped} more` : joined;
};

export const summarizeCondition = (
  c: ConditionDto,
  resolvers?: NameResolvers,
): RuleSummary => {
  const full = renderConditionMultiline(c, 0, resolvers);
  if (c.type === "field") {
    const short = renderFieldCondition(c.field, c.operator, c.value, resolvers);
    return { short, full };
  }
  if (c.type === "ref") {
    const short = renderRef(c.set, resolvers);
    return { short, full };
  }
  if (c.type === "retired") {
    const short = renderRetired(c.field);
    return { short, full };
  }
  // Top-level group — list children inline with truncation.
  if (c.conditions.length === 0) {
    const empty = c.type === "all" ? "(empty AND)" : "(empty OR)";
    return { short: empty, full: empty };
  }
  const childStrings = c.conditions.map((sub) =>
    sub.type === "all" || sub.type === "any"
      ? `(${renderConditionInline(sub, resolvers)})`
      : renderConditionInline(sub, resolvers),
  );
  const short = truncateJoin(
    childStrings,
    joinerFor(c.type),
    SHORT_LINE_BUDGET,
  );
  return { short, full };
};

// ----- actions -------------------------------------------------------------

const PROMPTABLE_LABEL: Record<FieldName, string> = FIELD_LABEL;

/**
 * `"a", "b"` — one action can name any number of categories. Both forms guard
 * against saving an empty one, but a rule posted straight to the API can hold
 * one, and a bare `+category` with nothing after it reads as a truncation.
 */
const renderCategoryList = (
  ids: readonly string[],
  resolvers: NameResolvers | undefined,
): string =>
  ids.length === 0
    ? "(none)"
    : ids
        .map(
          (id) =>
            `"${resolvers ? renderEntityValue(resolvers.category, id) : id}"`,
        )
        .join(", ");

/**
 * A conditional action's matcher, as an operator and a value.
 *
 * It names any number of entities, so it reads in the same vocabulary a rule
 * condition uses for `is` / `is one of` — `="A"` for one, `∈{"A", "B"}` for
 * several — because they now say the same thing. An empty matcher renders as
 * `(none)` rather than as nothing at all; both editors guard against saving
 * one, but a rule posted straight to the API can hold it.
 */
const matcherParts = (
  matcher: ConditionalActionMatcherDto,
  resolvers: NameResolvers | undefined,
): { op: string; value: string } => {
  if (matcher.field === "retired")
    return { op: "=", value: renderRetired(matcher.value) };
  const resolve = resolvers && resolvers[matcher.field];
  const names = matcher.value.map(
    (id) => `"${resolve ? renderEntityValue(resolve, id) : id}"`,
  );
  if (names.length === 0) return { op: "=", value: "(none)" };
  if (names.length === 1) return { op: "=", value: names[0] };
  return { op: "∈", value: `{${names.join(", ")}}` };
};

const renderAction = (a: EditActionDto, resolvers?: NameResolvers): string => {
  switch (a.field) {
    case "source":
      return `set ${FIELD_LABEL.source}="${
        resolvers ? renderEntityValue(resolvers.source, a.value) : a.value
      }"`;
    case "retired":
      return `set ${renderRetired(a.value)}`;
    case "group":
      return `set ${FIELD_LABEL.group}="${
        resolvers ? renderEntityValue(resolvers.group, a.value) : a.value
      }"`;
    case "expenseType":
      return `set ${FIELD_LABEL.expenseType}="${
        resolvers ? renderEntityValue(resolvers.expenseType, a.value) : a.value
      }"`;
    case "merchant":
    case "description":
      return `set ${FIELD_LABEL[a.field]}="${a.value}"`;
    case "amount":
      return `set amount=${a.value.amount}`;
    case "datePaid":
      return `set date=${a.value}`;
    case "foreignAmount":
      return a.value === null
        ? "clear foreign amount"
        : `foreign=${a.value.amount} ${a.value.currency}`;
    case "split":
      return `split=${a.value.amount}`;
    case "addCategory":
      return `+category ${renderCategoryList(a.value, resolvers)}`;
    case "removeCategory":
      return `−category ${renderCategoryList(a.value, resolvers)}`;
    case "addAttachment":
      return `+attachment ${truncId(a.value)}`;
    case "removeAttachment":
      return `−attachment ${truncId(a.value)}`;
    case "receipt":
      return a.value === null ? "clear receipt" : `receipt=${truncId(a.value)}`;
    case "invoice":
      return a.value === null ? "clear invoice" : `invoice=${truncId(a.value)}`;
    case "applyAutoTx":
      return `apply rule ${truncId(a.value)}`;
    // Writes nothing: the record that lists an invoice rule under the
    // transaction's applied rules.
    case "invoiceRuleApplied":
      return `record as applied (invoice "${a.value.name}")`;
    case "applyActionSet": {
      const name = resolvers?.actionSet?.(a.value.set) ?? truncId(a.value.set);
      // The values make two rules citing one set tell themselves apart, so
      // they belong in the one-line summary rather than only in the editor.
      const filled = a.value.inputs
        .filter((i) => i.value.trim() !== "")
        .map((i) => `${i.name}: ${i.value}`)
        .join(", ");
      return `apply action set "${name}"${filled ? ` (${filled})` : ""}`;
    }
    case "ignore":
      return a.value ? "ignore" : "un-ignore";
    case "prompt":
      return `ask user for ${PROMPTABLE_LABEL[a.value.field]}`;
    case "copyField":
      return `copy ${FIELD_LABEL[a.value.from]} → ${FIELD_LABEL[a.value.to]}`;
    case "conditional": {
      const { op, value } = matcherParts(a.value.condition, resolvers);
      const condBit = `${a.value.condition.field}${op}${value}`;
      return `if ${condBit} then ${renderAction(a.value.action, resolvers)}`;
    }
    case "match": {
      const field = a.value.arms[0]?.condition.field ?? "source";
      const arms = a.value.arms
        .map((arm) => {
          const { value } = matcherParts(arm.condition, resolvers);
          return `${value} → ${renderAction(arm.action, resolvers)}`;
        })
        .join("; ");
      return `match ${field}: ${arms || "(no arms)"}`;
    }
  }
};

const renderActionMultiline = (
  a: EditActionDto,
  resolvers: NameResolvers | undefined,
  indent = 0,
): string => {
  const pad = "  ".repeat(indent);
  if (a.field === "conditional") {
    const { op, value } = matcherParts(a.value.condition, resolvers);
    const cond = `${a.value.condition.field} ${op} ${value}`;
    return `${pad}if ${cond}\n${pad}  then ${renderAction(a.value.action, resolvers)}`;
  }
  if (a.field === "match") {
    const field = a.value.arms[0]?.condition.field ?? "source";
    const arms = a.value.arms.map((arm) => {
      const { value } = matcherParts(arm.condition, resolvers);
      return `${pad}  ${value} → ${renderAction(arm.action, resolvers)}`;
    });
    // The missing default is the point, so it is written out rather than left
    // to be inferred from what isn't there.
    return [
      `${pad}match ${field}`,
      ...arms,
      `${pad}  (no default — anything else is reported)`,
    ].join("\n");
  }
  return `${pad}${renderAction(a, resolvers)}`;
};

export const summarizeActions = (
  actions: EditActionDto[],
  resolvers?: NameResolvers,
): RuleSummary => {
  if (actions.length === 0) return { short: "no actions", full: "no actions" };
  const rendered = actions.map((a) => renderAction(a, resolvers));
  const short = truncateJoin(rendered, ", ", SHORT_LINE_BUDGET);
  const full = actions
    .map((a) => renderActionMultiline(a, resolvers))
    .join("\n");
  return { short, full };
};
